import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiClientError } from '../../lib/api';
import { useToast } from '../../components/Toast';
import Icon from '../../components/Icon';
import { Badge, EmptyState, PageLoading, SearchInput, Select } from '../../components/ui';
import { PageHeader } from '../../pages/AppShell';
import LeadDetail from './LeadDetail';
import LeadCreateDialog from './LeadCreateDialog';
import type { Lead, LeadList, LeadStage, LeadStatus, CustomerListColumn } from '../../types';

const CAT_TONE: Record<string, 'success' | 'warning' | 'neutral' | 'danger'> = {
  open: 'warning', won: 'success', lost: 'danger',
};

function cell(lead: Lead, column: CustomerListColumn): string {
  if (column.key === 'name') return lead.name;
  return lead.fields[column.key]?.display || '—';
}

export default function LeadsPage() {
  const toast = useToast();

  const [rows, setRows] = useState<Lead[]>([]);
  const [columns, setColumns] = useState<CustomerListColumn[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [stages, setStages] = useState<LeadStage[]>([]);
  const [statuses, setStatuses] = useState<LeadStatus[]>([]);
  const [search, setSearch] = useState('');
  const [stageId, setStageId] = useState('');
  const [statusId, setStatusId] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const debounced = useRef<number | null>(null);
  const [query, setQuery] = useState('');
  useEffect(() => {
    if (debounced.current) window.clearTimeout(debounced.current);
    debounced.current = window.setTimeout(() => setQuery(search), 350);
    return () => { if (debounced.current) window.clearTimeout(debounced.current); };
  }, [search]);

  useEffect(() => {
    (async () => {
      try {
        const [st, ss] = await Promise.all([
          api.get<LeadStage[]>('/leads/stages'),
          api.get<LeadStatus[]>('/leads/statuses'),
        ]);
        setStages(st); setStatuses(ss);
      } catch { /* filters are a convenience */ }
    })();
  }, []);

  const load = useCallback(async (cursor?: string) => {
    if (cursor) setLoadingMore(true); else setLoading(true);
    try {
      const data = await api.get<LeadList>('/leads', {
        search: query || undefined,
        stageId: stageId || undefined,
        statusId: statusId || undefined,
        cursor,
        limit: 50,
      });
      setColumns(data.columns);
      setRows((prev) => (cursor ? [...prev, ...data.leads] : data.leads));
      setNextCursor(data.nextCursor);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not load leads.');
    } finally { setLoading(false); setLoadingMore(false); }
  }, [query, stageId, statusId, toast]);

  useEffect(() => { void load(); }, [load]);

  const filtered = Boolean(query || stageId || statusId);

  return (
    <>
      <PageHeader
        title="Leads"
        subtitle="Your pipeline — every prospect, the stage it's at, and who's working it."
        actions={(
          <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
            <Icon name="plus" size={16} /> Add lead
          </button>
        )}
      />

      <div className="page">
        <div className="toolbar">
          <SearchInput value={search} onChange={setSearch} placeholder="Search by lead name" />
          <div style={{ width: 180 }}>
            <Select value={stageId} onChange={(e) => setStageId(e.target.value)} aria-label="Stage">
              <option value="">All stages</option>
              {stages.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </Select>
          </div>
          <div style={{ width: 180 }}>
            <Select value={statusId} onChange={(e) => setStatusId(e.target.value)} aria-label="Status">
              <option value="">Any status</option>
              {statuses.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </Select>
          </div>
          {filtered ? (
            <button type="button" className="btn btn-ghost btn-sm"
              onClick={() => { setSearch(''); setStageId(''); setStatusId(''); }}>
              <Icon name="x" size={14} /> Clear
            </button>
          ) : null}
        </div>

        <div className="card">
          {loading ? (
            <div className="card-pad"><PageLoading /></div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon="users"
              title={filtered ? 'No leads match those filters' : 'No leads yet'}
              body={filtered
                ? 'Try clearing a filter or searching for part of the name.'
                : 'Add your first lead, or let your team add them from the app.'}
            />
          ) : (
            <>
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      {columns.map((c) => <th key={c.key}>{c.label}</th>)}
                      <th>Stage</th>
                      <th>Status</th>
                      <th>Owner</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((lead) => (
                      <tr key={lead.id} className="clickable" onClick={() => setOpenId(lead.id)}>
                        {columns.map((c, i) => (
                          <td key={c.key} className={i === 0 ? 'primary' : undefined}>{cell(lead, c)}</td>
                        ))}
                        <td>{lead.stage?.label ?? '—'}</td>
                        <td>
                          <span className="row gap-8" style={{ alignItems: 'center' }}>
                            {lead.status?.category
                              ? <Badge tone={CAT_TONE[lead.status.category] ?? 'neutral'}>{lead.status.label}</Badge>
                              : (lead.status?.label ?? '—')}
                            {lead.converted ? <Badge tone="success">converted</Badge> : null}
                          </span>
                        </td>
                        <td>{lead.owner?.name ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="between" style={{ padding: '14px 18px', borderTop: '1px solid var(--border)' }}>
                <span className="small faint">{rows.length} lead{rows.length === 1 ? '' : 's'}{nextCursor ? ' so far' : ''}</span>
                {nextCursor ? (
                  <button type="button" className="btn btn-secondary btn-sm" disabled={loadingMore}
                    onClick={() => void load(nextCursor)}>
                    {loadingMore ? <span className="spinner" /> : null} Load more
                  </button>
                ) : null}
              </div>
            </>
          )}
        </div>
      </div>

      {openId ? (
        <LeadDetail leadId={openId} onClose={() => setOpenId(null)}
          onChanged={() => { void load(); }} />
      ) : null}
      {creating ? (
        <LeadCreateDialog onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); void load(); }} />
      ) : null}
    </>
  );
}
